import { TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import TimePickerCustom from 'src/components/common/field/TimePickerCustom';
import GenerateFieldChild from './GenerateFieldChild';

const inputStyle = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

const dateStyle = {
  width: '100%',
  height: '32px',
  my: '8px',
  '& .MuiInputBase-input.MuiOutlinedInput-input': {
    py: '8px',
    fontSize: '14px',
  },
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500',
};

const GenerateField = (props: any) => {
  const field = props.field;

  const handleInputChange = (event: any, name: string) => {
    props.handleChangeValue(name, {
      value: [event.target.value],
    });
  };

  const handleCheckboxChange = (value: any, name: string) => {
    let tempAnswer = [];
    if (!props.answer?.value) {
      tempAnswer = [value];
      props.handleChangeValue(name, {
        value: tempAnswer,
      });
      return;
    }
    tempAnswer = [...props.answer?.value];
    const index = props.answer?.value.indexOf(value);
    if (index > -1) {
      tempAnswer = [
        ...props.answer?.value.slice(0, index),
        ...props.answer?.value.slice(index + 1),
      ];
    } else {
      tempAnswer = [...props.answer?.value, ...[value]];
    }
    props.handleChangeValue(name, {
      value: tempAnswer,
    });
  };

  const handleRadioChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    const { value } = event.target;
    props.handleChangeValue(name, {
      value: [value],
    });
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null, name: string) => {
    props.handleChangeValue(name, {
      value: [newValue?.format('YYYY/MM/DD')],
    });
  };

  const handleSelectChange = (event: any, name: string) => {
    const { value } = event.target;
    props.handleChangeValue(name, {
      value: [value],
    });
  };

  const handleChangeTime = (name: string) => (date: Dayjs | null) => {
    const timeFormat = date ? date.format('HH:mm') : null;
    props.handleChangeValue(name, {
      value: [timeFormat],
    });
  };

  const handleChildChangeValue = (name: string, value: any) => {
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          dependent_answers: [''],
          value: [value],
        },
      },
    });
  };

  const handleChildDFChangeValue = (name: string, value: any) => {
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          value: props.answer?.children_answers ? [...props.answer?.children_answers[name].value] : [],
          dependent_answers: [value],
        },
      },
    });
  };

  const handleChildMultiChangeValue = (name: string, value: any) => {
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          dependent_answers: props.answer?.children_answers ? [...props.answer?.children_answers[name].dependent_answers] : [],
          value: value,
        },
      },
    });
  };

  const handleChildMultiDFChangeValue = (name: string, value: any) => {
    props.handleChangeValue(field.field_id, {
      ...props.answer,
      children_answers: {
        [name]: {
          value: props.answer?.children_answers ? [...props.answer?.children_answers[name].value] : [],
          dependent_answers: value,
        },
      },
    });
  };

  switch (field.type) {
    case 'text':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <OutlinedInput
            placeholder={field.help_text}
            sx={inputStyle}
            inputProps={{
              sx: {
                backgroundColor: 'primary.light',
              },
            }}
            name={field.field_id}
            value={props.answer}
            onChange={(event) => handleInputChange(event, field.field_id)}
          />
        </Box>
      );
    case 'number':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <OutlinedInput
            placeholder={field.help_text}
            sx={inputStyle}
            type='number'
            inputProps={{
              sx: {
                backgroundColor: 'primary.light',
              },
            }}
            name={field.field_id}
            value={props.answer}
            onChange={(event) => handleInputChange(event, field.field_id)}
          />
        </Box>
      );
    case 'textarea':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <TextField
            margin='dense'
            value={props.answer}
            placeholder={field.help_text}
            fullWidth
            multiline
            rows={field.rows || 4}
            onChange={(event) => handleInputChange(event, field.field_id)}
            sx={inputStyle}
          />
        </Box>
      );

    case 'checkbox':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          {field.options.map((option: any, idx: number) => {
            return (
              <FormControlLabel
                key={idx}
                control={
                  <Checkbox
                    checked={
                      props.answer && props.answer?.value ? props.answer?.value.includes(option) : false
                    }
                    onChange={() =>
                      handleCheckboxChange(option, field.field_id)
                    }
                  />
                }
                label={option}
                sx={{
                  '& .MuiSvgIcon-root': { fontSize: '18px' },
                  '& .MuiTypography-root': {
                    fontSize: '14px',
                    wordBreak: 'break-word',
                  },
                  mr: '10px',
                  width: '32%'
                }}
              />
            );
          })}
        </Box>
      );
    case 'radio':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <RadioGroup
            // row
            aria-labelledby='demo-controlled-radio-buttons-group'
            name={field.field_id}
            value={props.answer?.value || ''}
            onChange={(e) => handleRadioChange(e, field.field_id)}
          >
            {field.options.map((option: any, idx: number) => {
              return (
                <div key={idx}>
                  <FormControlLabel
                    value={option}
                    control={<Radio />}
                    label={option}
                  />
                  {field.children_fields &&
                    field.children_fields.map((cf: any, cfIdx: number) => {
                      if (cf.parent_id !== idx) return <div key={cfIdx}></div>;
                      return (
                        <GenerateFieldChild
                          key={cfIdx}
                          field={cf}
                          labelParent={option}
                          answer={props.answer}
                          handleChildChangeValue={handleChildChangeValue}
                          handleChildMultiChangeValue={handleChildMultiChangeValue}
                          handleChildDFChangeValue={handleChildDFChangeValue}
                          handleChildMultiDFChangeValue={handleChildMultiDFChangeValue}
                        />
                      );
                    })}
                </div>
              );
            })}
          </RadioGroup>
        </Box>
      );
    case 'date':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              name=''
              value={props.answer ? dayjs(props.answer) : null}
              onChange={(newValue) =>
                handleDateChange(newValue, field.field_id)
              }
              format='YYYY/MM/DD'
              sx={dateStyle}
            />
          </LocalizationProvider>
        </Box>
      );
    case 'time':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <TimePickerCustom
            value={dayjs(dayjs().format('YYYY-MM-DD') + ' ' + props.answer)}
            onChange={handleChangeTime(field.field_id)}
          />
        </Box>
      );
    case 'select':
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>

          <Select
            value={props.answer?.value || ' '}
            onChange={(e) => handleSelectChange(e, field.field_id)}
            sx={{
              backgroundColor: 'primary.light',
              width: '200px',
              '& .MuiInputBase-input.MuiOutlinedInput-input': {
                p: '6px 14px',
              },
            }}
          >
            <MenuItem value={' '}>{field.help_text}</MenuItem>
            {field.options.map((option: any, idx: number) => {
              return (
                <MenuItem value={option} key={idx}>
                  {option}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
      );

    default:
      return (
        <Box my={'5px'}>
          <Typography fontWeight={700}>
            {field.label || '-'}
            {field.required && (
              <Typography component='label' {...colorRequired}>
                *
              </Typography>
            )}
          </Typography>
          <OutlinedInput
            placeholder={field.help_text}
            sx={inputStyle}
            inputProps={{
              sx: {
                backgroundColor: 'primary.light',
              },
            }}
            name={field.field_id}
            value={props.answer?.value}
            onChange={(event) => handleInputChange(event, field.field_id)}
          />
        </Box>
      );
  }
};

export default GenerateField;
